import { useState } from "react";
import type { StoreKind, SubscriptionPlanView } from "@/lib/api/types";
import { Alert, Banner, Button, Checkbox, Field, Input, SearchSelect, useToast } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { toE164OrNull } from "@/lib/phone/phone";
import { isValidStoreSlug } from "@/lib/routing/store-slug";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { useCreateSellerMutation } from "../services/useCreateSellerMutation";
import { describeCreateError } from "./createErrors";
import { StoreSlugField } from "./StoreSlugField";

export type CreatedSellerCredentials = {
  sellerId: string;
  storeId: string;
  phone: string;
  temporaryPassword: string;
};

/**
 * A new seller account and its first business, in one step. The submit button
 * lives in the drawer's bar (`form={formId}`), so this renders fields only.
 * On success the parent gets the one-time credentials and shows them instead.
 */
export function SellerCreateForm({
  formId,
  locale,
  plans,
  storeKind = "DEFAULT",
  onCreated,
  onSubmittingChange,
}: {
  formId: string;
  locale: Locale;
  plans: SubscriptionPlanView[];
  storeKind?: StoreKind;
  onCreated: (credentials: CreatedSellerCredentials) => void;
  onSubmittingChange?: (submitting: boolean) => void;
}) {
  const t = useTranslations();
  const toast = useToast();
  const isDefault = storeKind === "DEFAULT";
  const legacyPlans = plans.filter(
    (plan) => plan.type !== "FREE" && plan.type !== "PREMIUM",
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phone, setPhone] = useState("");
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const [storeSlug, setStoreSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [premiumChoice, setPremiumChoice] = useState(false);
  // With no plan to pick, a restaurant can only be Premium. Derived rather than
  // set once, so switching the business type keeps it true.
  const isPremium = premiumChoice || (!isDefault && legacyPlans.length === 0);
  const { mutateAsync: createSeller } = useCreateSellerMutation();

  const setSubmitting = (value: boolean) => {
    setIsSubmitting(value);
    onSubmittingChange?.(value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["name", "storeName"],
      "",
    );
    // Each says how to fix its own field.
    if ("name" in requiredErrors) requiredErrors.name = t("admin.sellerNameRequired");
    if ("storeName" in requiredErrors) requiredErrors.storeName = t("admin.storeNameRequired");
    if (!isDefault && !isPremium && !subscriptionPlanId) {
      requiredErrors.subscriptionPlanId = t("admin.planRequired");
    }
    if (phone.trim().length === 0) {
      requiredErrors.phone = t("admin.sellerPhoneRequired");
    }
    setSlugTouched(true);
    if (isDefault && storeSlug !== "" && !isValidStoreSlug(storeSlug)) {
      requiredErrors.storeSlug = t(keys.admin.storeSlugInvalid);
    }

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      setSubmitting(false);
      return;
    }
    const normalizedPhone = toE164OrNull(phone);
    if (!normalizedPhone) {
      setFieldErrors({ phone: t(keys.buyer.trackingValidationPhone) });
      setSubmitting(false);
      return;
    }

    try {
      const payload = await createSeller({
        name: String(formData.get("name") ?? ""),
        email: String(formData.get("email") ?? "").trim() || undefined,
        phone: normalizedPhone,
        storeName: String(formData.get("storeName") ?? ""),
        storeSlug: isDefault && storeSlug ? storeSlug : undefined,
        storeKind,
        isPremium: isDefault ? false : isPremium,
        subscriptionPlanId:
          isDefault || isPremium ? undefined : subscriptionPlanId,
      });
      setSubmitting(false);
      toast.show({ message: t("admin.sellerCreatedToast", { name: payload.seller.name }) });
      onCreated({
        sellerId: payload.seller.id,
        storeId: payload.store.id,
        phone: normalizedPhone,
        temporaryPassword: payload.temporaryPassword,
      });
    } catch (caught) {
      // By the API's code, in Arabic, under the field to change — not the
      // server's English sentence in a banner.
      const { field, message } = describeCreateError(caught, t);
      if (field) setFieldErrors({ [field]: message });
      else setError(message);
      setSubmitting(false);
    }
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      {error ? (
        <Banner
          tone="error"
          title={t("admin.sellerCreateFailedTitle")}
          actions={
            <Button size="sm" type="submit" loading={isSubmitting} loadingLabel={t(keys.shell.retrying)}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {error} {t("admin.nothingCreated")}
        </Banner>
      ) : null}
      <Alert tone="info">
        {t(
          storeKind === "RESTAURANT"
            ? keys.admin.restaurantNewOwnerSummary
            : keys.admin.onlineStoreNewOwnerSummary,
        )}
      </Alert>
      <Field label={t(keys.common.name)} htmlFor="name" error={fieldErrors.name}>
        <Input
          id="name"
          name="name"
          placeholder={t(keys.placeholders.name)}
          invalid={Boolean(fieldErrors.name)}
        />
      </Field>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
          gap: 12,
        }}
      >
        <Field label={t(keys.common.phone)} htmlFor="phone" error={fieldErrors.phone}>
          <Input
            id="phone"
            name="phone"
            type="tel"
            dir="ltr"
            autoComplete="off"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+20 1xx xxx xxxx"
            invalid={Boolean(fieldErrors.phone)}
          />
        </Field>
        <Field label={t(keys.common.email)} htmlFor="email" error={fieldErrors.email}>
          <Input
            id="email"
            name="email"
            type="email"
            dir="ltr"
            placeholder={t(keys.placeholders.email)}
            invalid={Boolean(fieldErrors.email)}
          />
        </Field>
      </div>
      <Field
        label={t(
          storeKind === "RESTAURANT" ? keys.admin.restaurantName : keys.common.storeName,
        )}
        htmlFor="storeName"
        error={fieldErrors.storeName}
      >
        <Input
          id="storeName"
          name="storeName"
          placeholder={t(keys.placeholders.storeName)}
          invalid={Boolean(fieldErrors.storeName)}
        />
      </Field>
      {isDefault ? (
        <>
          <StoreSlugField
            value={storeSlug}
            onChange={(next) => {
              setStoreSlug(next);
              setFieldErrors((previous) => {
                const rest = { ...previous };
                delete rest.storeSlug;
                return rest;
              });
            }}
            error={fieldErrors.storeSlug}
            showInvalid={slugTouched}
            onBlur={() => setSlugTouched(true)}
          />
          <Alert tone="info">{t(keys.admin.tierCreateFree)}</Alert>
        </>
      ) : (
        <>
          <Checkbox
            label={t(keys.admin.storeCreatePremiumLabel)}
            description={t(keys.admin.storeCreatePremiumHelper)}
            checked={isPremium}
            disabled={legacyPlans.length === 0}
            onChange={setPremiumChoice}
          />
          {isPremium ? null : (
            <Field
              label={t(keys.admin.subscriptionSelectLabel)}
              htmlFor="subscriptionPlanId"
              error={fieldErrors.subscriptionPlanId}
            >
              <div style={{ maxWidth: 360 }}>
                <SearchSelect
                  id="subscriptionPlanId"
                  invalid={Boolean(fieldErrors.subscriptionPlanId)}
                  label={t(keys.admin.subscriptionSelectLabel)}
                  value={subscriptionPlanId}
                  onChange={setSubscriptionPlanId}
                  placeholder={t(keys.admin.subscriptionSelectPlaceholder)}
                  emptyLabel={t(keys.common.noResults)}
                  options={legacyPlans.map((plan) => ({
                    value: plan.id,
                    label: pickLocalized(locale, plan.nameAr, plan.nameEn),
                  }))}
                />
              </div>
            </Field>
          )}
        </>
      )}
    </form>
  );
}
